/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { FormHandles } from '@unform/core'
import { Form } from '@unform/web'
import moment from 'moment'
import * as Yup from 'yup'

import { FiUser, FiCheck, FiPercent, FiX } from 'react-icons/fi'
import { MdSecurity } from 'react-icons/md'
import { BiCake } from 'react-icons/bi'

import getValidationErrors from '../../utils/getValidationErrors'
import usePersistedState from '../../hooks/usePersistedState'
import validaCPF from '../../utils/validaCPF'

import InputSelect from '../../components/InputSelect'
import InputHidden from '../../components/InputHidden'
import Header from '../../components/Header'
import Button from '../../components/Button'
import Input from '../../components/Input'
import ModalBox from '../../components/Modal'

import {
  UserData,
  Participant,
  ParticipantDetails,
} from '../../utils/interfaces'
import {
  Content,
  BtnVoltar,
  BtnContato,
  RadioButton,
  BenefBox,
  ContentBenef,
} from './styles'
import calculaIdade from '../../utils/calculaIdade'
import Container from '../../components/Container'
import Line from '../../components/Line'

const Beneficiary: React.FC = () => {
  const [userData] = usePersistedState<UserData>('userData', {} as UserData)
  const [userDetails] = usePersistedState<ParticipantDetails>(
    'userDetails',
    {} as ParticipantDetails,
  )
  const [participants, setParticipants] = usePersistedState<Participant[]>(
    'participantsGroup',
    [],
  )
  const [, setSaveAndAddNew] = useState(false)
  const [thisParticipantData] = useState<UserData>({} as UserData)
  const [thisParticipant, setThisParticipant] = useState<Participant>(
    {} as Participant,
  )
  const [thisParticipantDetails] = useState<ParticipantDetails>(
    {} as ParticipantDetails,
  )

  const [vlrProporcao, setVlrProporcao] = useState(1)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [txtModal, setTxtModal] = useState('')
  const [tipoBenef, setTipoBenef] = useState('1')
  const [grauParent, setGrauParent] = useState({
    label: userDetails.dcrGrauParentesco,
    value: userDetails.grauParentesco,
  })

  const history = useHistory()
  const location = useLocation()
  const formRef = useRef<FormHandles>(null)

  const handleSubmit = useCallback(
    async formData => {
      try {
        formRef.current?.setErrors({})

        const schema = Yup.object().shape({
          name: Yup.string()
            .required('Seu nome é obrigatório.')
            .matches(/\s/g, 'Digite o nome completo')
            .min(3, 'Digite o nome completo'),
          birthdate: Yup.string()
            .required('Campo obrigatório.')
            .test(
              '',
              'A data de nascimento não pode ser maior que hoje.',
              () =>
                moment() >
                  moment(formData.birthdate.split('/').reverse().join('-')) ||
                formData.birthdate === '',
            )
            .test(
              '',
              'Data de nascimento inválida',
              () =>
                moment(
                  formData.birthdate.split('/').reverse().join('-'),
                ).isValid() || formData.birthdate === '',
            ),
          cpf: Yup.string()
            .required('Campo obrigatório.')
            .test('', 'CPF já utilizado em outro cadastro', function v() {
              const t2 =
                (participants.filter(
                  participant => participant.data.cpf === formData.cpf,
                ).length <= 0 &&
                  formData.cpf !== userData.cpf) ||
                formData.cpf === ''
              return t2
            })
            .test('', 'CPF inválido', function t() {
              const teste =
                validaCPF(formData.cpf.replaceAll('.', '').replace('-', '')) ||
                formData.cpf === ''
              return teste
            }),
        })

        await schema.validate(formData, { abortEarly: false })

        const dataForm = formData.birthdate.split('/').reverse().join('-')

        setThisParticipant({
          ...thisParticipant,
          data: {
            ...thisParticipantData,
            name: formData.name,
            cpf: formData.cpf,
            birthdate: formData.birthdate === '' ? '' : dataForm,
          },
          details: {
            ...thisParticipantDetails,
            tipoBen: tipoBenef,
            grauParentesco: grauParent.value,
            dcrGrauParentesco: grauParent.label,
            proporcao: vlrProporcao,
          },
        })

        setParticipants([
          ...participants,
          {
            ...thisParticipant,
            data: {
              ...thisParticipantData,
              name: formData.name,
              cpf: formData.cpf,
              birthdate: formData.birthdate === '' ? '' : dataForm,
            },
            details: {
              ...thisParticipantDetails,
              tipoBen: tipoBenef,
              grauParentesco: grauParent.value,
              dcrGrauParentesco: grauParent.label,
              proporcao: vlrProporcao,
            },
          },
        ])
        if (formData.addNew === 'true') {
          history.push(`${location.pathname}`)
          formRef.current?.reset()
        } else {
          history.push('/beneficiary-list')
        }
      } catch (err) {
        formRef.current?.setErrors(
          getValidationErrors(err as Yup.ValidationError),
        )
      }
    },
    [
      grauParent,
      history,
      location.pathname,
      participants,
      setParticipants,
      thisParticipant,
      thisParticipantData,
      thisParticipantDetails,
      tipoBenef,
      userData,
      vlrProporcao,
    ],
  )

  const handleChangeGrauParentesco = useCallback(e => {
    const t = e
    setGrauParent(t)
  }, [])

  const handleAddNovo = useCallback(() => {
    setSaveAndAddNew(true)
    const field = formRef.current?.getFieldRef('addNew')
    field.value = true
    formRef.current?.submitForm()
  }, [])

  const handleJustSave = useCallback(() => {
    setSaveAndAddNew(false)
    const field = formRef.current?.getFieldRef('addNew')
    field.value = false
    formRef.current?.submitForm()
  }, [])

  const arr = participants.map(participant => participant.details.proporcao)
  let soma = 0
  for (let i = 0; i < arr.length; i += 1) {
    soma += arr[i]
  }

  const handleValidaProporcao = useCallback((prop, s) => {
    if (prop < 1 || prop > s) {
      const msg = `A proporção deve ser maior que 1 e menor que ${s}`
      setTxtModal(msg)
      setIsModalOpen(true)
    } else {
      setVlrProporcao(prop)
    }
  }, [])

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false)
  }, [])

  const handleRemove = useCallback(
    id => {
      setParticipants(() =>
        participants.filter((participant, idx) => idx !== id),
      )
    },
    [participants, setParticipants],
  )

  useEffect(() => {
    const benefBox = document.getElementById('benefBox') as HTMLDivElement
    const altura = benefBox.clientHeight
    window.scrollTo({
      top: altura,
      left: 0,
      behavior: 'smooth',
    })
  }, [])

  return (
    <>
      <Header />
      <Container>
        <Content>
          <div id="benefBox" style={{ width: '100%' }}>
            {participants.length > 0 ? (
              <BenefBox>
                <h3>Beneficiários</h3>
                {participants.map((participant, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <ContentBenef key={index}>
                    <div>
                      <FiX onClick={() => handleRemove(index)} />
                      <div>
                        <strong>Vínculo: </strong>
                        <label>{participant.details.dcrGrauParentesco}</label>
                      </div>
                      <div>
                        <strong>Nome: </strong>
                        <label>{participant.data.name}</label>
                      </div>
                      <div>
                        <strong>Data de nascimento: </strong>
                        <label>
                          {participant.data.birthdate
                            .split('-')
                            .reverse()
                            .join('/')}
                        </label>
                      </div>
                      <div>
                        <strong>CPF: </strong>
                        <label>{participant.data.cpf}</label>
                      </div>
                    </div>
                    <Line />
                  </ContentBenef>
                ))}
              </BenefBox>
            ) : null}
          </div>
          <br />
          <h3>Informe seus beneficiários:</h3>
          <p>
            O cônjuge e os filhos menores de 21 anos já são beneficiários legais
            automáticos. Na ausência dos beneficiários legais os beneficiários
            indicados deverão receber seus recursos financeiros.
          </p>
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={{
              name: thisParticipantData.name,
              cpf: thisParticipantData.cpf,
              tipoBen: thisParticipantDetails.tipoBen,
              birthdate:
                thisParticipantData.birthdate === undefined
                  ? ''
                  : thisParticipantData.birthdate
                      .split('-')
                      .reverse()
                      .join('/'),
              grauParentesco: grauParent.value,
              dcrGrauParentesco: grauParent.label,
            }}
          >
            <InputHidden name="addNew" type="hidden" />
            <InputSelect
              name="grauParentesco"
              value={grauParent}
              options={[
                { label: 'Pai ou Mãe', value: '5' },
                { label: 'Filho(a)', value: '20' },
                { label: 'Irmão ou Irmã', value: '6' },
                { label: 'Avô ou Avó', value: '12' },
                { label: 'Tio(a)', value: '13' },
                { label: 'Neto(a)', value: '14' },
                { label: 'Sobrinho(a)', value: '15' },
                { label: 'Ex-esposo(a)', value: '16' },
                { label: 'Genro ou Nora', value: '17' },
                { label: 'Sogro(a)', value: '18' },
                { label: 'Concubino(a)', value: '19' },
                { label: 'Outros', value: '0' },
              ]}
              placeholder="Tipo de Vínculo"
              onChange={e => handleChangeGrauParentesco(e)}
            />

            <Input placeholder="Nome completo" name="name" icon={FiUser} />
            <Input
              placeholder="CPF"
              name="cpf"
              icon={MdSecurity}
              type="tel"
              mask="cpf"
              required
            />
            <Input
              icon={BiCake}
              name="birthdate"
              mask="date"
              placeholder="Data de nascimento"
            />
          </Form>

          <Button
            type="button"
            width="small"
            fontSize="small"
            onClick={handleAddNovo}
          >
            Adicionar mais um beneficiário
          </Button>
        </Content>

        {participants.length > 0 ? (
          <>
            <Button
              type="button"
              fontSize="normal"
              color="blue"
              onClick={() => history.push('/beneficiary-list')}
            >
              <span>Continuar</span>
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              fontSize="normal"
              color="blue"
              onClick={handleJustSave}
            >
              <span>Continuar</span>
            </Button>
            <Button
              type="button"
              fontSize="normal"
              color="orange"
              onClick={() => history.push('/ppe-fatca')}
            >
              <span>Não tenho beneficiários</span>
            </Button>
          </>
        )}

        <BtnVoltar type="button" onClick={() => history.push('/address-info')}>
          &lt; Anterior
        </BtnVoltar>
        <ModalBox isOpen={isModalOpen} onRequestClose={handleCloseModal}>
          {txtModal}
        </ModalBox>
      </Container>
    </>
  )
}

export default Beneficiary
